import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { notificationAPI, authAPI } from '../utils/api';

const TYPE_ICON = {
  task_assigned: '📋',
  task_updated: '🔄',
  deadline_soon: '⏰',
  team_added: '👥',
  impersonation_started: '🎭',
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread]               = useState(0);
  const [open, setOpen]                   = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await notificationAPI.getAll({ limit: 15 });
      setNotifications(data.notifications);
      setUnread(data.unreadCount);
    } catch {}
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    await notificationAPI.markRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAll = async () => {
    await notificationAPI.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnread(0);
  };

  // Target-side "End session" for impersonation notifications.
  // The recipient hits /api/auth/impersonate/stop from their normal token —
  // the backend resolves the active session by targetUserId and ends it.
  const endImpersonationSession = async (notificationId) => {
    try {
      await authAPI.stopImpersonation();
      toast.success('Impersonation session ended.');
      if (notificationId) markRead(notificationId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'No active impersonation session found.');
    }
  };

  return (
    <div className="relative" ref={ref}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg transition-colors"
        style={{ color: 'var(--text-tertiary)' }}
        onMouseEnter={e => {
          e.currentTarget.style.color = 'var(--text-primary)';
          e.currentTarget.style.background = 'var(--hover-bg)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.color = 'var(--text-tertiary)';
          e.currentTarget.style.background = 'transparent';
        }}
        aria-label="Notifications"
      >
        🔔
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-1rem)] rounded-xl z-50 overflow-hidden"
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--border-muted)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--border-subtle)' }}
          >
            <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Notifications
            </span>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs transition-colors"
                style={{ color: 'var(--accent-text)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--brand-primary)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--accent-text)')}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto custom-scroll">
            {notifications.length === 0 ? (
              <p className="text-center py-8 text-sm" style={{ color: 'var(--text-faint)' }}>
                All caught up! 🎉
              </p>
            ) : notifications.map(n => (
              <div
                key={n.id}
                onClick={() => !n.isRead && markRead(n.id)}
                className="px-4 py-3 cursor-pointer transition-colors"
                style={{
                  borderBottom: '1px solid var(--border-subtle)',
                  background: n.isRead ? 'transparent' : 'var(--accent-bg-soft)',
                  opacity: n.isRead ? 0.65 : 1,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = n.isRead ? 'transparent' : 'var(--accent-bg-soft)';
                }}
              >
                <div className="flex gap-3">
                  <span className="text-lg flex-shrink-0">{TYPE_ICON[n.type] || '🔔'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium break-words" style={{ color: 'var(--text-primary)' }}>
                      {n.title}
                    </p>
                    <p className="text-xs mt-0.5 line-clamp-2 break-words" style={{ color: 'var(--text-tertiary)' }}>
                      {n.message}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
                      {new Date(n.createdAt).toLocaleString()}
                    </p>
                    {n.type === 'impersonation_started' && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); endImpersonationSession(n.id); }}
                        className="text-xs font-semibold mt-2 px-2 py-1 rounded"
                        style={{
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        End session
                      </button>
                    )}
                  </div>
                  {!n.isRead && (
                    <div
                      className="w-2 h-2 rounded-full mt-1 flex-shrink-0"
                      style={{ background: 'var(--brand-primary)' }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
